import { Button, Grid, Typography } from '@material-ui/core'
import CircularProgress from '@material-ui/core/CircularProgress'
import withWidth, { isWidthDown, WithWidth } from '@material-ui/core/withWidth'
import FilterFramesIcon from '@material-ui/icons/FilterFrames'
import QueryBuilderIcon from '@material-ui/icons/QueryBuilder'
import cn from 'classnames'
import isToday from 'date-fns/is_today'
import moment from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import { Chart } from 'react-google-charts'
import { connect, useDispatch } from 'react-redux'
import { compose } from 'recompose'
import { bindActionCreators } from 'redux'
import {
  ORDER_STATUS_CANCELLED,
  ORDER_STATUS_COMPLETED,
  ORDER_STATUS_NEW,
  ORDER_STATUS_TO_BE_CONFIRMED,
} from '../../Constants/Order/Order.constants'
import withServices from '../../Contexts/Services/WithServices.context'
import { createOrderConcreteDeliveriesPageLink } from '../../Pages/OrderConcreteDeliveries/OrderConcreteDeliveries.page.route'
import i18nLocaleSelector from '../../State/Store/I18n/Selectors/I18nLocale.selector'
import ordersActionCreators from '../../State/Store/Orders/Orders.actions'
import orderConcreteDeliveriesStatus from '../../State/Store/Orders/Selectors/OrderConcreteDeliveriesStatus.selector'
import orderItemSelector from '../../State/Store/Orders/Selectors/OrderItem.selector'
import { UI_TRUCKS_PANE_VIEW_TRUCKS_BTN, UI_ORDER_PROGRESS_PLUS_LOAD_CALL_BORAL_CLICK } from '../../State/Store/UI/UI.types'
import formatWithTime from '../../Support/DateTime/FormatWithTime/DateTimeFormatWithTime.support'
import isNumber from '../../Support/Is/Number/IsNumber.support'
import Spinner from '../Spinner/Spinner.component'
import GraphLegend from './parts/GraphLegend.comp'
import { useHistory } from 'react-router-dom'
import hitGTM from 'State/Store/UI/Actions/GTM.action'
import uiTriggered from 'State/Store/UI/Actions/UI.Triggered.action'
import { PourMethod, formatPourMethod } from 'types/PourMethod'
import { formatTruckSize, TruckSize } from 'types/TruckSize'
import { shouldShowPlusLoad } from 'Components/OrderDetailsHeader/parts/utils/should-show-plus-load'
import { Order, PlusLoadStatus } from 'types/Order'
import OrderProgressStyles from './styles'
import { withGlobalStyles } from 'Support/Theme/CreateInjectableStyles/withGlobalStyles'
import { WithGlobalStylesProps } from 'types/styles/WithGlobalStylesProps'
import { getPieChartOptions } from './utils/get-pie-chart-options'
import { getPieChartData, normalizeDeliveryDetails } from './utils/get-pie-chart-data'
import sidebarActionCreators from '../../State/Store/Sidebar/Sidebar.actions';

interface Props {
  actions: any,
  currentDeliveryDetails: any,
  currentOrder: Order,
  locale: any,
  orderId: any,
}
type FProps = Props & WithGlobalStylesProps<any> & WithWidth

const OrderProgress = (props: FProps) => {
  const { actions, classes, currentDeliveryDetails, currentOrder, locale, orderId, width } = props
  const history = useHistory()
  const dispatch = useDispatch()
  const isSmall = isWidthDown(`xs`, width)
  const isWidthDownMd = isWidthDown(`md`, width)
  const [ completed, setCompleted ] = useState(0)
  const graphRef = useRef<any>()
  const [ loading, setLoading ] = useState<boolean>(false)
  const [ chartReady, setChartReady ] = useState<boolean>(false)
  const [ options, setOptions ] = useState<any>({})

  useEffect(() => {
    setOptions(getPieChartOptions(isSmall, currentOrder))
  }, [ isSmall ])

  useEffect(() => setLoading(true), [])

  useEffect(() => {
    (async () => {
      const { summary, billToAccount } = currentOrder || {}
      if (!summary) {
        return
      }
      const { orderCode, orderLine } = summary
      const requestPayload = {
        billToAccount,
        orderCode: orderCode.trim(),
        orderLine,
        status: `DELIVERED,INTRANSIT,SCHEDULED`,
      }
      await actions.ordersFindByStatus(requestPayload)
      setLoading(false)
    })()
    return () => {

    }
  }, [currentOrder])

  if (!currentOrder || !currentOrder.details || !currentDeliveryDetails.data) {
    return null
  }

  const { details } = currentOrder
  const { qty } = details
  const qtyDispatched: number = (qty && qty.dispatched) as number

  let normalizedDetails: any = {
    delivered: { loadQty: 0 },
    inTransit: { loadQty: 0 },
    scheduled: { loadQty: 0 },
  }


  if (currentOrder.summary.status === ORDER_STATUS_COMPLETED) {
    normalizedDetails.delivered.loadQty = qtyDispatched
    normalizedDetails = {
      delivered: {
        loadQty: Number((qtyDispatched * 1).toFixed(1)) * 1,
      },
      inTransit: { loadQty: 0 },
      scheduled: { loadQty: Number((currentOrder.details.qty.ordered - qtyDispatched).toFixed(1)) * 1 },
    }
  } else if (
    currentOrder.summary.status === ORDER_STATUS_TO_BE_CONFIRMED
    || currentOrder.summary.status === ORDER_STATUS_CANCELLED
    || moment(formatWithTime(currentOrder.summary.startTime, `MM/DD/YYYY`, {})).isAfter(moment().endOf(`day`))
  ) {
    normalizedDetails.scheduled.loadQty = 1
    if (options && options.legend) {
      options.legend.position = `none`
    }
  } else if (currentDeliveryDetails.data.orders.loads) {
    normalizedDetails = normalizeDeliveryDetails(currentDeliveryDetails)
  }

  // check for edge caes when delivered is more than ordered and it causes scheduled value to be negative
  if (normalizedDetails.scheduled.loadQty < 0) {
    normalizedDetails.scheduled.loadQty = 0
  }

  normalizedDetails.plusOrder = details.plusLoad

  const data = getPieChartData(normalizedDetails, isSmall, currentOrder.details.qty.ordered, currentOrder.summary.status)
  const link = createOrderConcreteDeliveriesPageLink({ orderId })

  /**
   * VerticalGridItem
   * @returns {React.Component} The component
   */
  const VerticalGridItem = () => {
    return (
      <Grid item>
        <div className={classes.detailsBoxItemBorder}>&nbsp;</div>
      </Grid>
    )
  }

  const hasViewLoad = isToday(currentOrder.summary.startTime.replace(`.`, `:`))
    && currentOrder.summary.status !== ORDER_STATUS_TO_BE_CONFIRMED
    && currentOrder.summary.status !== ORDER_STATUS_NEW
    && currentOrder.summary.status !== ORDER_STATUS_CANCELLED

  /**
   * Render the DetailsItem component
   * @param {Object} details icon, title and content to be rendered
   * @returns {React.Component} The rendered `<DetailsItem />`
   * @memberof OrderProgressComponent
   */
  const DetailsItem = ({ icon, title, content }: any) => {
    return (
      <Grid container>
        <Grid item xs={12}>
          <Grid container spacing={1} className={cn(classes.flexWrapNowrap, classes.boxHeading)}>
            {icon && (
              <Grid item className={classes.detailsItemIcon}>
                {icon}
              </Grid>
            )}
            <Grid item>
              <Typography variant='subtitle2' className={cn(classes.textAlignLeft, classes.boxHeading)}>
                {title}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          {content}
        </Grid>
      </Grid>
    )
  }

  const callBoral = (boralTel: string) => {
    dispatch(hitGTM(UI_ORDER_PROGRESS_PLUS_LOAD_CALL_BORAL_CLICK))
    window.open(`tel:${boralTel}`, `_self`)
  }

  const handleViewTrucks = () => {
    actions.sidebarRightToggle();
    dispatch(uiTriggered({ type: UI_TRUCKS_PANE_VIEW_TRUCKS_BTN }))
  }

  if (loading) {
    return <Spinner />
  }
  return (
    <Grid className={classes.textAlignCenter} container>
      <Grid className={classes.deliveryTotalContainer} item xs={12}>
        <Grid container>
          <Grid item xs={4}>
            <GraphLegend
              classes={classes}
              currentOrder={currentOrder}
              currentDeliveryDetails={currentDeliveryDetails}
              normalizedDetails={normalizedDetails}
              callBoral={callBoral}
            />
          </Grid>
          <Grid item xs={8}>
            <div className={classes.graphItem}>
              <div className={classes.deliveryTotalContent} id='DeliveryTotal' ref={graphRef}>
                {chartReady && (
                  <React.Fragment>
                    <Grid container>
                      <Grid item xs={12}>
                        <Typography variant='body2' className={classes.pieChartTitle}>
                          { `Total` }
                        </Typography>
                        <Typography variant='h4'>
                          { `${currentOrder.details.qty.ordered}m³` }
                        </Typography>
                        { shouldShowPlusLoad(currentOrder)
                          && currentOrder.details.plusLoadDetails.status === PlusLoadStatus.SUBMITTED
                          && (
                            <Typography variant='h6'>
                              { `+ ${currentOrder.details.plusLoadDetails.qty} m³ ` }
                            </Typography>
                          )
                        }
                      </Grid>
                    </Grid>
                  </React.Fragment>
                )}
              </div>

              <Chart
                loader={<Spinner />}
                chartEvents={[
                  {
                    eventName: `ready`,
                    callback({ chartWrapper }) {
                      if (!chartReady) {
                        setChartReady(true)
                      }
                    },
                  },
                ]}
                chartType='PieChart'
                data={data}
                options={options}
              />
            </div>
          </Grid>
        </Grid>
      </Grid>

      <Grid className={classes.etaBoxSm} item xs={12}>
        <Grid container spacing={2} className={classes.marginBottom3unit}>
          <Grid item xs={4}>
            <Grid container spacing={1} className={classes.flexWrapNowrap}>
              {currentOrder.summary.status !== ORDER_STATUS_CANCELLED && currentOrder.summary.status !== ORDER_STATUS_COMPLETED && (
                <Grid item className={classes.timerContainer}>
                  <QueryBuilderIcon className={classes.timerIcon} />
                  <CircularProgress size={14} className={cn(classes.circularProgress)} value={completed} variant='static' />
                </Grid>
              )}
              <Grid item>
                <Typography variant='subtitle2' className={classes.boxHeading}>
                  { `Next Truck ETA` }
                </Typography>
              </Grid>
            </Grid>
            <Grid container spacing={1} className={classes.textAlignCenter}>
              <Grid item>{getEtaText(normalizedDetails, currentOrder, classes, locale)}</Grid>
            </Grid>
          </Grid>
          <VerticalGridItem />
          <Grid item xs={3}>
            <Typography variant='subtitle2' className={classes.boxHeading}>
              { `Truck Interval` }
            </Typography>
            <Typography variant='body1'>{currentOrder.details.spacing || currentOrder.details.qty.ordered}</Typography>
          </Grid>
          {/* currentOrder.summary.startTime does not seem to be in valid ISO format hence need to fix it before passing to isToday. It also causes console warnings from momentJS */}
          {hasViewLoad && isWidthDownMd ? (
            <React.Fragment>
              <VerticalGridItem />
              <Grid item xs={3}>
                <Button
                  color='primary'
                  fullWidth={isWidthDown(`xs`, width)}
                  onClick={handleViewTrucks}
                  variant='outlined'
                  size='small'
                >
                  <Grid container justify='space-between'>
                    { `View Trucks` }
                  </Grid>
                </Button>
              </Grid>
            </React.Fragment>
          ) : null}
        </Grid>
        <Grid container spacing={1}>
          {currentOrder.details.pourMethod && (
            <Grid item xs={4} className={classes.textAlignLeft}>
              <DetailsItem
                icon={<FilterFramesIcon fontSize='small' />}
                content={
                <Typography variant='body1'>
                  { formatPourMethod(currentOrder.details.pourMethod as PourMethod) || details.pourMethodDescription }
                </Typography>
                }
                title={`Pour Method`}
              />
            </Grid>
          )}
          {currentOrder.details.truckSize && currentOrder.details.pourMethod && <VerticalGridItem />}
          {currentOrder.details.truckSize && (
            <Grid item xs={4} className={classes.textAlignLeft}>
              <DetailsItem
                icon={<FilterFramesIcon fontSize='small' />}
                content={
                <Typography variant='body1'>
                  {formatTruckSize(currentOrder.details.truckSize as TruckSize)}
                </Typography>
                }
                title={`Max Truck Size`}
              />
            </Grid>
          )}
        </Grid>

        <hr className={cn(classes.marginTop3unit, classes.keyLine)} />
      </Grid>
    </Grid>
  )
}

/**
 * getEtaText
 * @param {Object} normalizedDetails Details of the ordwer
 * @param {Object} classes Style classes
 * @returns {React.Component} The component
 */
const getEtaText = (normalizedDetails: any, currentOrder: any, classes: any, locale: any) => {
  if (currentOrder.summary.status === ORDER_STATUS_TO_BE_CONFIRMED) {
    return (
      <Typography className={cn(classes.nextEtaTime, classes.textAlignLeft)} variant='h6'>
        tbc
      </Typography>
    )
  }
  if (currentOrder.summary.status === ORDER_STATUS_COMPLETED) {
    return null
  }

  const options = { hour: `numeric`, minute: `numeric`, timeZone: `UTC` }

  return (
    <Typography variant='h6'>
      {isNumber(+normalizedDetails.nextEtaTime) && Object.keys(normalizedDetails.inTransit).length !== 0
        ? new Intl.DateTimeFormat(locale.lang, options).format(new Date(+normalizedDetails.nextEtaTime))
        : `-`}
    </Typography>
  )
}

export default compose<FProps, Props>(
  withWidth(),
  withServices,
  withGlobalStyles(OrderProgressStyles),
  connect(
    (state, ownProps) => {
      const { orderId, services } = ownProps as any
      const orderEntitySelector = orderItemSelector(orderId)
      const currentOrder = orderEntitySelector(state)
      const { summary } = currentOrder || {}
      const { orderCode, orderLine } = summary || {}
      const status = `DELIVERED,INTRANSIT,SCHEDULED`
      const statusId = services.orderService.getStatusId({ orderCode: orderCode ? orderCode.trim() : ``, orderLine, status })
      const orderConcreteDeliveriesStatusSelector = orderConcreteDeliveriesStatus(statusId)
      const currentDeliveryDetails = orderConcreteDeliveriesStatusSelector(state) || {}

      return {
        currentDeliveryDetails,
        currentOrder,
        locale: i18nLocaleSelector(state),
      }
    },
    (dispatch) => ({
      actions: bindActionCreators(
        {
          ...ordersActionCreators,
          ...sidebarActionCreators,
        },
        dispatch,
      ),
    }),
  ),
)(OrderProgress)
